/**
 * Role-Based Access Control (RBAC) Permissions
 *
 * Permissions are fetched from the API at login / auth-check time via
 * fetchRolePermissions().  All helper functions read from the in-memory
 * _rolePermissions map — no hardcoded defaults live here.
 *
 * Access levels:
 *   'full'      — User can view and perform all write actions
 *   'read-only' — User can view but all write actions (create, edit, delete, toggle) are disabled
 *   'disabled'  — Menu item is visible but greyed out / not clickable; route redirects if navigated to directly
 *   'hidden'    — Route/menu item is completely hidden and inaccessible
 *
 * Each role has a 'default' key that acts as a fallback for any route not explicitly listed.
 * Section-level overrides use the format 'section:subsection' (e.g., 'settings:aeo').
 */

import { authApiClient, registerReadOnlyChecker } from './api-client';

export type AccessLevel = 'full' | 'read-only' | 'disabled' | 'hidden';

export type RolePermissions = {
  default: AccessLevel;
  [routeOrSection: string]: AccessLevel;
};

// ── Runtime permissions store ────────────────────────────────────────────
// Populated by fetchRolePermissions(), read by all helper functions.

let _rolePermissions: Record<string, RolePermissions> = {};

/**
 * Normalize permissions from the API response.
 * Supports two formats:
 *   1. Flat: { default: "full", "/route": "read-only", ... }
 *   2. Nested: { default: "full", permissions: { "/route": "read-only", ... } }
 * Both are normalized to the flat format used by all helper functions.
 */
function normalizePermissions(
  data: Record<string, any>,
): Record<string, RolePermissions> {
  const result: Record<string, RolePermissions> = {};
  for (const [role, config] of Object.entries(data)) {
    if (config.permissions && typeof config.permissions === 'object') {
      const { permissions, ...rest } = config;
      result[role] = { ...rest, ...permissions };
    } else {
      result[role] = config;
    }
  }
  return result;
}

/**
 * Fetch role permissions from the API and cache them in memory.
 * Should be called once after authentication succeeds (login or page-load auth check).
 *
 * Returns true if the API call succeeded, false if it failed.
 * On failure a safe fallback is used so super-admins are never locked out.
 */
export async function fetchRolePermissions(): Promise<boolean> {
  try {
    const { data } = await authApiClient.get<Record<string, any>>(
      '/tenant/roles/permissions',
    );
    _rolePermissions = normalizePermissions(data);
    return true;
  } catch (error) {
    console.error('Failed to fetch role permissions, using safe fallback:', error);
    // Safe fallback — super_admin can always access everything
    _rolePermissions = {
      super_admin: { default: 'full' },
    };
    return false;
  }
}

/**
 * Clear cached permissions (call on logout).
 */
export function clearRolePermissions(): void {
  _rolePermissions = {};
}

/**
 * Whether permissions have been loaded from the API.
 */
export function isPermissionsLoaded(): boolean {
  return Object.keys(_rolePermissions).length > 0;
}

// ── Helper Functions ─────────────────────────────────────────────────────

/**
 * Routes that take another route's level when a role doesn't set their own,
 * so roles set up before the route existed reach it the same way.
 */
const ROUTE_ACCESS_FALLBACK: Record<string, string> = {
  // The Domain Expert app's home screen is part of its settings (same ai-api settings).
  '/expert-agents/home-screen': '/expert-agents/settings',
};

/**
 * Get the access level for a given role and route/section.
 * Falls back to the route in ROUTE_ACCESS_FALLBACK, then to the role's 'default' access level.
 */
export function getAccessLevel(role: string | undefined, path: string): AccessLevel {
  if (!role) return 'hidden';

  const roleLower = role.toLowerCase();
  const roleConfig = _rolePermissions[roleLower];

  if (!roleConfig) return 'hidden';

  // Check exact match first
  if (roleConfig[path] !== undefined) {
    return roleConfig[path];
  }

  const fallbackPath = ROUTE_ACCESS_FALLBACK[path];
  if (fallbackPath && roleConfig[fallbackPath] !== undefined) {
    return roleConfig[fallbackPath];
  }

  // Return role default
  return roleConfig.default;
}

/**
 * Check if a user can access a route (not hidden or disabled).
 */
export function canAccess(role: string | undefined, path: string): boolean {
  const level = getAccessLevel(role, path);
  return level !== 'hidden' && level !== 'disabled';
}

/**
 * Check if a route/menu item is disabled (visible but not clickable).
 */
export function isDisabledAccess(role: string | undefined, path: string): boolean {
  return getAccessLevel(role, path) === 'disabled';
}

/**
 * Check if a menu item should be visible (not hidden — includes full, read-only, and disabled).
 */
export function isMenuVisible(role: string | undefined, path: string): boolean {
  return getAccessLevel(role, path) !== 'hidden';
}

/**
 * Check if a route is read-only for the given role.
 */
export function isReadOnlyAccess(role: string | undefined, path: string): boolean {
  return getAccessLevel(role, path) === 'read-only';
}

/**
 * Check if a route has full (read-write) access for the given role.
 */
export function hasFullAccess(role: string | undefined, path: string): boolean {
  return getAccessLevel(role, path) === 'full';
}

/**
 * Get the access level for a settings section (e.g., 'aeo', 'contacts').
 * Falls back to the /settings route-level permission.
 */
export function getSettingsSectionAccess(role: string | undefined, section: string): AccessLevel {
  const sectionKey = `settings:${section}`;
  const level = getAccessLevel(role, sectionKey);

  // If the section key wasn't explicitly defined, fall back to /settings route level
  if (!role) return 'hidden';
  const roleLower = role.toLowerCase();
  const roleConfig = _rolePermissions[roleLower];
  if (roleConfig && roleConfig[sectionKey] !== undefined) {
    return level;
  }

  // Fallback to /settings page-level permission
  return getAccessLevel(role, '/settings');
}

// ── Register with api-client interceptor (defense-in-depth) ──────────────
// This allows the axios write-blocking interceptor to check route-level
// permissions directly at request time, independent of React lifecycle.
registerReadOnlyChecker(isReadOnlyAccess);
